import { PaperAirplaneIcon } from "@heroicons/react/24/solid";
import type { TFunction } from "i18next";
import { useId, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { newClientKey, postMessage } from "../../../api/threads/threadsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import BlockedState from "../../../ui/feedback/BlockedState";
import ForkIcon from "../../../ui/icons/ForkIcon";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import ChooseAgent from "../hosting/ChooseAgent";
import { useThreadContext } from "../page/threadPageContext";
import { asDecision } from "../page/viewerActions";
import { describeComposer, type AgentOutlook, type ComposerFacts } from "./answeringState";
import ComposerSuggestionList from "./ComposerSuggestionList";
import { mentionsOf, toolsStillIn, type PickedTool } from "./mentions";
import TurnControls from "./TurnControls";
import { useComposerSuggestions } from "./useComposerSuggestions";
import { insertReferenceAt } from "./suggestions";
import { LibraryPicker } from "../../search";
import type { ItemKind } from "../../../api/content/reviewDecoders";
import Field from "../../../ui/forms/Field";
import { suggestionOptionId } from "../../../ui/forms/suggestionNavigation";
import ToolRunForm from "./ToolRunForm";
import { messageWithReferences, ReferenceChips, removeFromChat, takeFromChat, useChatDestination, useChatReferences } from "../../chatReferences";

/** What Add… finds: library items `@` also inserts, by the same reference. */
const ADDABLE_KINDS: readonly ItemKind[] = ["artifact", "tool"];

/** The outlook in words; why a route can't run is worded once, as the LLM choice words it. */
function outlookWords(outlook: Exclude<AgentOutlook, "needsRoute">, t: TFunction): string {
  if (typeof outlook === "string") return t(`threads.composer.outlook.${outlook}`);
  return t("threads.composer.outlook.cannotRun", { reason: t(`threads.hosting.unavailable.${outlook.cannotRun}`) });
}

/** What `@` or Add… picked: an `@` suggestion or a library item. */
interface PickedItem {
  readonly kind: string;
  readonly reference: string;
  readonly name: string;
}

interface ComposerProps {
  /** Everything about the Thread except the draft. */
  facts: ComposerFacts;
  onFork: () => void;
}

/**
 * Work with the agent in the Thread (F70; FR-THREAD-3, 7 as amended): Markdown and entry
 * references. `@` inserts context, documents, artifacts, tools and the writer's own memories;
 * `/` inserts skills and prompts (`suggestions.ts`); Add… finds an artifact or tool with the
 * library search and inserts it at the caret (F38). A tool inserted either way is given to the
 * agent, whose turn may run it, and can be run here (W2-6). What was selected in another view
 * and sent to the chat shows as chips and goes with the next message (F96). Every message is a request to the
 * agent; people take part through the Workflow's person steps, not here. It says when the agent won't
 * answer at once, and keeps a message queued for the agent's next turn while it works (T-6).
 * Enter sends; Shift+Enter breaks.
 */
export default function Composer({ facts, onFork }: ComposerProps) {
  const { t } = useTranslation();
  const suggestionListId = useId();
  const { threadId, overview } = useThreadContext();
  const { projectId } = overview;
  const textArea = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState("");
  const [caret, setCaret] = useState(0);
  const [pickedTools, setPickedTools] = useState<PickedTool[]>([]);
  const [runningTool, setRunningTool] = useState<string | null>(null);
  // The last message sent and its key: a retry of the same message reuses the key, any other
  // message (other text, chips or tools) gets a new one (Sol S3).
  const [attempt, setAttempt] = useState<{ readonly body: string; readonly key: string } | null>(null);
  const { state: sending, submit } = useSubmission();
  // What was selected elsewhere and sent here (F96), carried by the next message.
  const references = useChatReferences(projectId);
  const outlook = describeComposer(facts);
  // Where a reference sent to the chat shows: this composer, and whether it takes messages (TR-B3-03).
  useChatDestination({ projectId, threadId, title: overview.title, takesMessages: outlook.availability.canWrite });

  const placeCaret = (next: number) => {
    requestAnimationFrame(() => {
      textArea.current?.focus();
      textArea.current?.setSelectionRange(next, next);
    });
  };
  /** The draft after `@` or Add… inserted a reference; a tool is remembered, so the message gives it to the agent. */
  const insertReference = (inserted: { readonly text: string; readonly caret: number }, item: PickedItem) => {
    setText(inserted.text);
    setCaret(inserted.caret);
    if (item.kind === "tool") setPickedTools((current) => [...current, { reference: item.reference, name: item.name }]);
    placeCaret(inserted.caret);
  };
  const { suggestions, activeIndex, someFailed, choose, handleKey } = useComposerSuggestions({
    text,
    caret,
    projectId,
    onInsert: insertReference,
  });

  if (!outlook.availability.canWrite) {
    return (
      <div className="app-surface space-y-2 rounded border p-3">
        {outlook.availability.reason === "missingPermission"
          ? <BlockedState decision={asDecision(facts.posting, projectId)} />
          : <p className="app-text text-sm">{t(`threads.composer.cannotWrite.${outlook.availability.reason}`)}</p>}
        {/* What was sent to the chat stays in view: it goes with a fork's first message (TR-B3-01). */}
        {references.length === 0 ? null : (
          <>
            <ReferenceChips references={references} onRemove={(reference) => { removeFromChat(projectId, reference); }} />
            <p className="app-text-muted text-xs">{t("chatReferences.withFork")}</p>
          </>
        )}
        <AppButton variant="secondary" size="sm" title={t("threads.fork.explanation")} onClick={onFork}>
          <ForkIcon />
          {t("threads.fork.atLatest")}
        </AppButton>
        {outlook.agentBusy ? <TurnControls /> : null}
      </div>
    );
  }

  const send = () => {
    const message = messageWithReferences(text, references);
    if (message === "" || sending.status === "submitting") return;
    const sent = references;
    const body = { text: message, mentions: mentionsOf(message, pickedTools) };
    const signature = JSON.stringify(body);
    const clientMessageId = attempt?.body === signature ? attempt.key : newClientKey();
    setAttempt({ body: signature, key: clientMessageId });
    submit(async () => {
      await postMessage(threadId, { ...body, clientMessageId });
      takeFromChat(projectId, sent);
      setText("");
      setCaret(0);
      setPickedTools([]);
      setRunningTool(null);
      setAttempt(null);
    });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (handleKey(event)) return;
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      send();
    }
  };

  return (
    <div className="app-surface space-y-2 rounded border p-3">
      {outlook.agentBusy ? <TurnControls /> : null}
      <ReferenceChips references={references} onRemove={(reference) => { removeFromChat(projectId, reference); }} />
      <div className="relative">
        <ComposerSuggestionList id={suggestionListId} suggestions={suggestions} activeIndex={activeIndex} onChoose={choose} />
        <Field labelHidden label={t("threads.composer.label")}>
          {(control) => (
            <textarea
              {...control}
              ref={textArea}
              rows={3}
              className={`${FORM_CONTROL_CLASS} resize-y`}
              aria-autocomplete="list"
              aria-controls={suggestions.length > 0 ? suggestionListId : undefined}
              aria-activedescendant={suggestions.length > 0 ? suggestionOptionId(suggestionListId, activeIndex) : undefined}
              value={text}
              placeholder={t("threads.composer.placeholder")}
              onChange={(event) => {
                setText(event.target.value);
                setCaret(event.target.selectionStart);
              }}
              onSelect={(event) => { setCaret(event.currentTarget.selectionStart); }}
              onKeyDown={onKeyDown}
            />
          )}
        </Field>
        {someFailed ? <p className="app-text-muted mt-1 text-xs">{t("threads.composer.suggestions.someFailed")}</p> : null}
      </div>
      {toolsStillIn(text, pickedTools).map((tool) => (
        <div key={tool.reference} className="space-y-2">
          <AppButton variant="secondary" size="sm" aria-expanded={runningTool === tool.reference} onClick={() => { setRunningTool(runningTool === tool.reference ? null : tool.reference); }}>
            {t("threads.composer.toolRun.open", { tool: tool.name })}
          </AppButton>
          {runningTool === tool.reference ? <ToolRunForm tool={tool} canRun={!outlook.agentBusy} onRequested={() => { setRunningTool(null); }} /> : null}
        </div>
      ))}
      {sending.status === "failed" ? <ErrorNotice error={sending.error} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        <p className="app-text-muted min-w-0 flex-1 text-xs" aria-live="polite">
          {outlook.agentOutlook === null ? null : outlook.agentOutlook === "needsRoute" ? <ChooseAgent /> : outlookWords(outlook.agentOutlook, t)}
        </p>
        <LibraryPicker
          projectId={projectId}
          kinds={ADDABLE_KINDS}
          buttonLabel={t("threads.composer.add")}
          searchLabel={t("threads.composer.addSearch")}
          align="right"
          onPick={(item) => { insertReference(insertReferenceAt(text, caret, item.reference), item); }}
        />
        <AppButton variant="primary" size="sm" onClick={send} disabled={(text.trim() === "" && references.length === 0) || sending.status === "submitting"}>
          <PaperAirplaneIcon className="h-4 w-4" aria-hidden="true" />
          {outlook.agentBusy ? t("threads.composer.queue") : t("threads.composer.send")}
        </AppButton>
      </div>
    </div>
  );
}
